import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, ChevronRight } from 'lucide-react';
import { mediaUrl } from '../../lib/programsApi';

const SERIF = { fontFamily: "'Playfair Display', serif" };

const StatusPill = ({ p }) => {
  if (p.status === 'COMPLETED') return <span className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full bg-[#7a9b8e]/15 text-[#5c8477] font-semibold" data-testid={`program-status-${p.slug}`}>Completed</span>;
  if (p.status === 'IN_PROGRESS') return <span className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full bg-[#D6AD60]/15 text-[#a88a4c] font-semibold" data-testid={`program-status-${p.slug}`}>In progress</span>;
  return <span className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full bg-stone-200/70 text-stone-500 font-semibold" data-testid={`program-status-${p.slug}`}>Not started</span>;
};

export const ProgramCard = ({ p, highlighted, ribbon }) => {
  const navigate = useNavigate();
  const cta = p.status === 'IN_PROGRESS' ? 'Continue Journey' : p.status === 'COMPLETED' ? 'View Program' : 'Begin Journey';
  const started = p.status !== 'NOT_STARTED';
  const shownDay = p.status === 'COMPLETED' ? p.totalDays : p.currentDay;
  return (
    <button
      type="button"
      id={`flagship-${p.slug}`}
      onClick={() => navigate(`/programs/${p.slug}`)}
      className={`relative w-full text-left rounded-3xl overflow-hidden bg-[#FFFCF3] border shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow,border-color] duration-300 scroll-mt-24 ${highlighted ? 'border-[#D6AD60] ring-2 ring-[#D6AD60]/30' : 'border-[#e3d2a8]/40'}`}
      data-testid={`flagship-card-${p.slug}`}
    >
      {ribbon && (
        <span className="absolute top-5 -right-9 z-10 rotate-45 w-40 text-center py-1 bg-[#D6AD60] text-[#0f1e33] text-[10px] font-semibold uppercase tracking-[0.22em] shadow-md" data-testid={`flagship-ribbon-${p.slug}`}>
          {ribbon}
        </span>
      )}
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        {p.coverImageUrl ? (
          <img src={mediaUrl(p.coverImageUrl, 800)} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
        ) : <div className="w-full h-full bg-[#1e3a5f]" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f1e33]/40 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full bg-white/85 text-[#1e3a5f] font-semibold backdrop-blur">{p.category}</span>
          {p.recommendedFirst && <span className="text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full bg-[#D6AD60] text-[#0f1e33] font-semibold">Start here</span>}
        </div>
        {p.badgeEarned && (
          <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#D6AD60] text-[#0f1e33] flex items-center justify-center shadow-md" title={p.badgeName || 'Badge earned'} data-testid={`flagship-badge-${p.slug}`}>
            <Award className="w-4.5 h-4.5" />
          </div>
        )}
      </div>
      <div className="p-5">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#a88a4c] mb-1">{p.totalDays} {p.dayLabel || 'Day'}s · One short session a day</p>
        <h3 className="text-2xl font-bold text-[#0f1e33] leading-tight mb-2" style={SERIF}>{p.title}</h3>
        <p className="text-sm text-[#2d4a70] leading-relaxed mb-4">{p.tagline}</p>
        <div className="flex items-center justify-between mb-2">
          <StatusPill p={p} />
          {started && (
            <span className="text-xs font-semibold text-[#1e3a5f]" data-testid={`flagship-progress-${p.slug}`}>
              {p.dayLabel || 'Day'} {shownDay} of {p.totalDays} · {p.percentComplete}%
            </span>
          )}
        </div>
        <div className="h-1.5 bg-[#efe3ce] rounded-full overflow-hidden mb-4">
          <div className="h-full bg-[#D6AD60] rounded-full transition-[width] duration-700" style={{ width: `${started ? p.percentComplete : 0}%` }} />
        </div>
        {p.badgeEarned && p.completions > 1 && <p className="text-[11px] text-[#7a9b8e] mb-3">Completed {p.completions} times</p>}
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#0f1e33]" data-testid={`flagship-cta-${p.slug}`}>
          {cta} <ChevronRight className="w-4 h-4 text-[#D6AD60]" />
        </span>
      </div>
    </button>
  );
};
